<!--
// app/layouts/teamPage.vue
-->
<script setup lang="ts">
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import TheHeader from "~/app/components/layout/TheHeader.vue";
import TheFooter from "~/app/components/layout/TheFooter.vue";
import { useTeamAndPlayerStore } from "~/app/stores/teamAndPlayerStore";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useWebappStore } from "~/app/stores/webappStore";
import { useTeams } from "~/app/composables/teams";
import { usePlayers } from "~/app/composables/players";
import regionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import { useWebContext } from "~/app/composables/webContext";

const route = useRoute();
const { region, loadState } = useCompetitionStore();
const { webContext } = useWebContext();

const reloadCompetitionStore = async (season: Season) => {
  const webappStore = useWebappStore();

  const { getRegions } = regionUiService();
  const regions = await getRegions(webContext);
  const activeRegion = regions.find(
    r => r.competitionId.length && r.competitionId?.includes(season.competitionId)
  );

  await loadState(activeRegion);
  webappStore.setActiveRegion();
};

if (!region) {
  const teamAndPlayerStore = useTeamAndPlayerStore();

  if (route.params.team) {
    const { findTeamCurrentOrRecentSeason, useFetchParamsTeamSeasons } = useTeams();

    teamAndPlayerStore.teamId = Number(route.params.team);
    const { data } = await useFetch(useFetchParamsTeamSeasons(teamAndPlayerStore.teamId));
    teamAndPlayerStore.teamSeasonsPlayed = data.value ? data.value : undefined;
    teamAndPlayerStore.teamLastSeasonPlayed = teamAndPlayerStore.teamSeasonsPlayed
      ? findTeamCurrentOrRecentSeason(teamAndPlayerStore.teamSeasonsPlayed)
      : undefined;

    if (teamAndPlayerStore.teamLastSeasonPlayed)
      await reloadCompetitionStore(teamAndPlayerStore.teamLastSeasonPlayed);
  } else if (route.params.playerId) {
    const { useFetchParamsPlayerSeasons, findLastSeasonPlayedByPlayer } = usePlayers();

    teamAndPlayerStore.playerId = Number(route.params.playerId);
    const { data } = await useFetch(useFetchParamsPlayerSeasons(teamAndPlayerStore.playerId));
    teamAndPlayerStore.playerSeasonsPlayed = data.value ? data.value : undefined;
    teamAndPlayerStore.playerLastSeasonPlayed = teamAndPlayerStore.playerSeasonsPlayed
      ? findLastSeasonPlayedByPlayer(teamAndPlayerStore.playerSeasonsPlayed.seasons)
      : undefined;

    if (teamAndPlayerStore.playerLastSeasonPlayed)
      await reloadCompetitionStore(teamAndPlayerStore.playerLastSeasonPlayed);
  } else {
    const webappStore = useWebappStore();
    const { getRegions } = regionUiService();
    const regions = await getRegions(webContext);
    const activeRegion = regions.find(r => r.id === 1);

    await loadState(activeRegion);
    webappStore.setActiveRegion();
  }
}

onErrorCaptured((err, target, info) => {
  if (err.statusCode !== 404) {
    console.error("Layout error: ", err, info);
    return false;
  }
});
</script>

<template>
  <the-header></the-header>
  <main class="main">
    <slot />
  </main>
  <the-footer></the-footer>
</template>

<style scoped>
.main {
  min-height: calc(theme(height.screen) - 20vh);
}
</style>
